import { useState } from 'react'
import { useSession } from '../lib/session'
import { useConnection, useRefreshingMutation, useToggleTask, useWeek } from '../lib/queries'
import { api } from '../lib/api'
import { TaskList } from '../components/TaskList'
import { Avatar, Empty, ErrorText, Loading, Pill, Sheet } from '../components/ui'
import { PlusIcon } from '../components/Icons'

export function Week() {
  const { viewedMemberId, members, isParent, session } = useSession()
  const { data, isLoading } = useWeek(viewedMemberId)
  const toggle = useToggleTask(viewedMemberId, isParent)
  const [connectionOpen, setConnectionOpen] = useState(false)

  const logReward = useRefreshingMutation((taskId: string) =>
    api.complete(taskId, viewedMemberId ?? undefined),
  )
  const removeConnection = useRefreshingMutation((id: string) => api.deleteConnection(id))
  const approve = useRefreshingMutation((completionId: string) => api.approve(completionId))

  if (isLoading) return <Loading />
  if (!data) return <Empty title="Nothing to show yet" />

  const personalDone = data.personalWeekly.filter((t) => t.approved).length
  const nameOf = (id: string): string => members.find((m) => m.id === id)?.name ?? 'Someone'

  return (
    <div className="page">
      <div className="row-between">
        <h1 className="title">This week</h1>
        <Pill tone="accent">{data.pointsThisWeek} pts</Pill>
      </div>

      <section className="card">
        <div className="row-between">
          <h2 className="title">Weekly responsibilities</h2>
          <Pill tone={personalDone === data.personalWeekly.length ? 'ok' : 'default'}>
            {personalDone}/{data.personalWeekly.length}
          </Pill>
        </div>
        <TaskList
          tasks={data.personalWeekly}
          onToggle={(task, next) => toggle.mutate({ taskId: task.id, done: next })}
          byParent={isParent}
          viewerId={session?.memberId ?? null}
        />
      </section>

      <section className="card">
        <h2 className="title">Shared spaces</h2>
        <p className="subtitle">Clean one shared space each week.</p>
        <TaskList
          tasks={data.householdWeekly}
          showClaimant
          onToggle={(task, next) => toggle.mutate({ taskId: task.id, done: next })}
          onApproveClaim={(task) => (task.claimedBy ? approve.mutate(task.claimedBy.completionId) : undefined)}
          byParent={isParent}
          viewerId={session?.memberId ?? null}
          emptyTitle="No shared spaces set up"
        />
        <ErrorText error={toggle.error ?? approve.error} />
      </section>

      <section className="card">
        <div className="row-between">
          <h2 className="title">Connecting with family</h2>
          {data.connectionMet ? <Pill tone="ok">Done this week</Pill> : <Pill tone="warn">Not yet</Pill>}
        </div>
        <p className="subtitle">At least one non-solo connection with family per week.</p>

        {data.connectionEvents.length > 0 ? (
          <div className="list">
            {data.connectionEvents.map((event) => (
              <div key={event.id} className="check check-done" style={{ cursor: 'default' }}>
                <span className="check-body">
                  <span className="check-title">{event.title}</span>
                  <span className="check-detail">
                    {event.happenedOn} · {event.participants.map(nameOf).join(', ')}
                  </span>
                  {event.note ? <span className="check-detail">{event.note}</span> : null}
                </span>
                <button
                  type="button"
                  className="btn btn-quiet"
                  aria-label={`Remove ${event.title}`}
                  onClick={() => removeConnection.mutate(event.id)}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        ) : null}

        <button type="button" className="btn btn-primary btn-block" onClick={() => setConnectionOpen(true)}>
          <PlusIcon /> Log something we did together
        </button>
      </section>

      <section className="card">
        <h2 className="title">Extra reward jobs</h2>
        <p className="subtitle">Optional, repeatable, and worth the most points.</p>
        <div className="list">
          {data.rewards.map((task) => (
            <div key={task.id} className="check" style={{ cursor: 'default' }}>
              <span className="check-body">
                <span className="check-title">{task.title}</span>
                {task.detail ? <span className="check-detail">{task.detail}</span> : null}
              </span>
              <button
                type="button"
                className="btn btn-ghost"
                disabled={logReward.isPending}
                onClick={() => logReward.mutate(task.id)}
              >
                +{task.points}
              </button>
            </div>
          ))}
        </div>

        {data.rewardsLogged.length > 0 ? (
          <>
            <hr className="divider" />
            <p className="section-title">Logged this week</p>
            <div className="list">
              {data.rewardsLogged.map((entry) => (
                <div key={entry.completionId} className="row-between">
                  <span className="muted">
                    {entry.title}
                    {entry.approved ? null : <span className="check-waiting-text"> · waiting for a parent</span>}
                  </span>
                  <span className="check-points">+{entry.points}</span>
                </div>
              ))}
            </div>
          </>
        ) : null}
        <ErrorText error={logReward.error} />
      </section>

      <ConnectionSheet open={connectionOpen} onClose={() => setConnectionOpen(false)} />
    </div>
  )
}

function ConnectionSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { members, viewedMemberId } = useSession()
  const { data } = useConnection()
  const [title, setTitle] = useState('')
  const [ideaId, setIdeaId] = useState<string | null>(null)
  const [note, setNote] = useState('')
  const [happenedOn, setHappenedOn] = useState(() => new Date().toISOString().slice(0, 10))
  const [participants, setParticipants] = useState<string[]>(() =>
    viewedMemberId ? [viewedMemberId] : [],
  )

  const log = useRefreshingMutation(() =>
    api.logConnection({
      title: title.trim(),
      ideaId,
      happenedOn,
      participants,
      note: note.trim() || null,
    }),
  )

  const toggleParticipant = (id: string): void => {
    setParticipants((current) =>
      current.includes(id) ? current.filter((x) => x !== id) : [...current, id],
    )
  }

  return (
    <Sheet open={open} title="What did you do together?" onClose={onClose}>
      <div className="field">
        <label className="label" htmlFor="connection-title">
          The activity
        </label>
        <input
          id="connection-title"
          className="input"
          value={title}
          onChange={(event) => {
            setTitle(event.target.value)
            setIdeaId(null)
          }}
          placeholder="Beach walk and boba"
        />
      </div>

      {data?.ideas.length ? (
        <div className="field">
          <span className="label">Or pick from the list</span>
          <div className="chips">
            {data.ideas.slice(0, 24).map((idea) => (
              <button
                key={idea.id}
                type="button"
                className={`chip${ideaId === idea.id ? ' chip-on' : ''}`}
                onClick={() => {
                  setIdeaId(idea.id)
                  setTitle(idea.title)
                }}
              >
                {idea.title}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <div className="field">
        <span className="label">Who was there? (at least two)</span>
        <div className="chips">
          {members.map((member) => (
            <button
              key={member.id}
              type="button"
              className={`chip${participants.includes(member.id) ? ' chip-on' : ''}`}
              onClick={() => toggleParticipant(member.id)}
            >
              <Avatar emoji={member.emoji} size="sm" /> {member.name}
            </button>
          ))}
        </div>
      </div>

      <div className="field">
        <label className="label" htmlFor="connection-date">
          When
        </label>
        <input
          id="connection-date"
          className="input"
          type="date"
          value={happenedOn}
          onChange={(event) => setHappenedOn(event.target.value)}
        />
      </div>

      <div className="field">
        <label className="label" htmlFor="connection-note">
          Note
        </label>
        <input
          id="connection-note"
          className="input"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder="Optional"
        />
      </div>

      <ErrorText error={log.error} />
      <button
        type="button"
        className="btn btn-primary btn-block btn-lg"
        disabled={!title.trim() || participants.length < 2 || log.isPending}
        onClick={() =>
          log.mutate(undefined, {
            onSuccess: () => {
              setTitle('')
              setNote('')
              setIdeaId(null)
              onClose()
            },
          })
        }
      >
        Log it
      </button>
    </Sheet>
  )
}
